/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { dataFetcher, endpoints } from 'Api';
import { Pathname } from 'Routes';
import AlterImage from 'Assets/alter-image.png';
import RowCarousel from 'Components/reactbits/RowCarousel';
import { AnimatedChevronLeft, AnimatedPlay } from 'Components/reactbits/AnimatedIcons';
import { useViewerMode } from 'hooks/useViewerMode';
import vm from '../video-details/index.module.css';

// Individual album photos carry the image URL in `photo_file` (see ImageGroup).
const photoImg = (ph) => ph?.photo_file || ph?.image || ph?.icon || ph?.imagekit_icon
  || ph?.thumbnails?.[0] || ph?.url || AlterImage;

/**
 * Netflix-style in-place album modal. Opens over the profile (blurred content
 * behind) instead of navigating to /album/:id. Reuses the video modal's chrome
 * (vm* classes) for a consistent look: big preview + info on top, a divider,
 * then the album's photos as a Magic-Bento row. Clicking a photo swaps the
 * preview in place; Play opens the album player.
 */
export const AlbumModal = ({ albumID, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [album, setAlbum] = useState(null);
  const [active, setActive] = useState(0);
  const { isFullscreen, toggle: toggleViewerMode } = useViewerMode();

  // Fullscreen/popover toggle button (per-user preference), shared by both states.
  const viewerToggleBtn = (
    <button
      type="button"
      className={vm.vmExpand}
      onClick={(e) => { e.stopPropagation(); toggleViewerMode(); }}
      aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
      title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
    >
      {isFullscreen ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 3v3a2 2 0 0 1-2 2H3" />
          <path d="M21 8h-3a2 2 0 0 1-2-2V3" />
          <path d="M3 16h3a2 2 0 0 1 2 2v3" />
          <path d="M16 21v-3a2 2 0 0 1 2-2h3" />
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M8 3H5a2 2 0 0 0-2 2v3" />
          <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
          <path d="M3 16v3a2 2 0 0 0 2 2h3" />
          <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
        </svg>
      )}
    </button>
  );

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setActive(0);
    dataFetcher(endpoints.getMyAlbumDetail, { album_id: albumID })
      .then((res) => {
        if (!alive) return;
        setAlbum(res?.data?.[0] || null);
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [albumID]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && onClose) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const photos = album?.photos || [];
  const preview = photos[active] ? photoImg(photos[active]) : album?.icon || AlterImage;

  const handleCardGlow = (e) => {
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--gx', `${e.clientX - r.left}px`);
    el.style.setProperty('--gy', `${e.clientY - r.top}px`);
  };

  const openPlayer = () => {
    const photoId = photos[active]?.id || photos[0]?.id;
    if (photoId != null) navigate(Pathname.getAlbumPlayerPath(albumID, photoId));
  };

  return (
    <div className={isFullscreen ? `${vm.vmOverlay} ${vm.vmOverlayFull}` : vm.vmOverlay} onClick={onClose} aria-hidden="true">
      {loading ? (
        <div className={isFullscreen ? `${vm.vmCard} ${vm.vmCardFull}` : vm.vmCard} onClick={(e) => e.stopPropagation()} aria-hidden="true">
          <button className={vm.vmClose} onClick={onClose} type="button" aria-label="Close">
            <AnimatedChevronLeft size={20} animate />
          </button>
          {viewerToggleBtn}
          <div className={vm.vmCardLoading}>
            <div className={vm.vmShimmer} />
          </div>
        </div>
      ) : (
        <div className={isFullscreen ? `${vm.vmCard} ${vm.vmCardFull}` : vm.vmCard} onClick={(e) => e.stopPropagation()} aria-hidden="true">
          <button className={vm.vmClose} onClick={onClose} type="button" aria-label="Close">
            <AnimatedChevronLeft size={20} animate />
          </button>
          {viewerToggleBtn}
          <div className={vm.vmTop}>
            <div className={vm.vmPreview} style={{ backgroundImage: `url(${preview})` }} />
            <div className={vm.vmInfo}>
              <h2 className={vm.vmTitle}>{album?.title || 'Album'}</h2>
              <div className={vm.vmMeta}>
                {photos.length}
                {' '}
                photo
                {photos.length === 1 ? '' : 's'}
              </div>
              {album?.description ? <p className={vm.vmDesc}>{album.description}</p> : null}
              <div className={vm.vmActions}>
                <button className={vm.vmPlay} onClick={openPlayer} type="button">
                  <AnimatedPlay size={18} animate />
                  {' '}
                  Play
                </button>
              </div>
            </div>
          </div>
          <div className={vm.vmDivider} />
          <div className={vm.vmRelated}>
            <div className={vm.vmRelatedTitle}>Photos in this album</div>
            {photos.length === 0 ? (
              <div className={vm.vmLoading}>No photos in this album.</div>
            ) : (
              <RowCarousel gap={10}>
                {photos.map((ph, i) => (
                  <div
                    key={ph.id || i}
                    className={vm.vmRelTile}
                    style={{ backgroundImage: `url(${photoImg(ph)})` }}
                    onMouseMove={handleCardGlow}
                    onClick={() => setActive(i)}
                    aria-hidden="true"
                  >
                    {ph.title ? <div className={vm.vmRelName}>{ph.title}</div> : null}
                  </div>
                ))}
              </RowCarousel>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AlbumModal;
